/**
 * GMB Insights Page
 *
 * Displays performance analytics from Google My Business:
 * - Views (Search + Maps)
 * - Actions (Website clicks, Phone calls, Direction requests)
 * - Search keywords that drive traffic
 * - Trends over time with Recharts visualization
 * - Location breakdown
 * - CSV export capability
 * - Goal tracking with KPI targets
 *
 * Data source: gmb_insights table (synced via syncInsights in gmbSync.ts)
 * Keywords source: gmb_locations.search_keywords (synced via getSearchKeywords)
 * Goals source: gmb_performance_goals table
 */

import React, { useState, useMemo, useCallback } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { DatePicker } from '@/components/ui/date-picker'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Eye,
  MousePointer,
  Phone,
  MapPin,
  Globe,
  Calendar,
  RefreshCw,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Search,
  Target,
  Plus,
  Trash2,
  Edit2,
  Database,
  AlertTriangle
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { format, subDays, isAfter, isBefore, subMonths } from 'date-fns'
import { syncInsights } from '@/utils/gmbSync'
import { useToast } from '@/hooks/use-toast'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

interface OutletContext {
  locations: any[]
  selectedLocation: string | null
  setSelectedLocation: (id: string) => void
  selectedLocationData: any
}

interface InsightRow {
  id: string
  gmb_location_id: string
  date: string
  views_search: number
  views_maps: number
  actions_website: number
  actions_phone: number
  actions_directions: number
  photo_views: number
  bookings: number
  messages: number
}

interface SearchKeyword {
  keyword: string
  impressions: number
}

/**
 * Performance goal for tracking KPIs
 * Stored in gmb_performance_goals table
 */
interface PerformanceGoal {
  id: string
  gmb_location_id: string | null
  metric_type: string
  target_value: number
  period_type: 'weekly' | 'monthly' | 'quarterly'
  is_active: boolean
  notes: string | null
  created_at: string
}

// Metric type options for goal setting
const METRIC_OPTIONS = [
  { value: 'views_search', label: 'Search Views', icon: Eye },
  { value: 'views_maps', label: 'Maps Views', icon: MapPin },
  { value: 'actions_website', label: 'Website Clicks', icon: Globe },
  { value: 'actions_phone', label: 'Phone Calls', icon: Phone },
  { value: 'actions_directions', label: 'Direction Requests', icon: MapPin },
  { value: 'total_views', label: 'Total Views', icon: Eye },
  { value: 'total_actions', label: 'Total Actions', icon: MousePointer },
] as const

interface AggregatedMetrics {
  totalViewsSearch: number
  totalViewsMaps: number
  totalActionsWebsite: number
  totalActionsPhone: number
  totalActionsDirections: number
  totalPhotoViews: number
  previousViewsSearch: number
  previousViewsMaps: number
  previousActionsWebsite: number
  previousActionsPhone: number
  previousActionsDirections: number
}

type DateRange = '7d' | '30d' | '90d' | 'custom'

export default function GMBInsights() {
  const { locations } = useOutletContext<OutletContext>()
  const [dateRange, setDateRange] = useState<DateRange>('30d')
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(subDays(new Date(), 30))
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(new Date())
  const [selectedLocationFilter, setSelectedLocationFilter] = useState<string>('all')
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncingLocationId, setSyncingLocationId] = useState<string | null>(null)
  const [syncDays, setSyncDays] = useState<number>(90) // Default to 90 days
  const toast = useToast()
  const queryClient = useQueryClient()

  // Goal modal state
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false)
  const [editingGoal, setEditingGoal] = useState<PerformanceGoal | null>(null)
  const [goalForm, setGoalForm] = useState<{
    metric_type: string
    target_value: string
    period_type: 'weekly' | 'monthly' | 'quarterly'
    gmb_location_id: string
    notes: string
  }>({
    metric_type: 'views_search',
    target_value: '',
    period_type: 'monthly',
    gmb_location_id: 'all',
    notes: ''
  })

  // Calculate date range based on selection
  const { startDate, endDate, previousStartDate, dateRangeDays } = useMemo(() => {
    const now = new Date()
    let start: Date
    let end: Date
    let days: number

    if (dateRange === 'custom' && customStartDate && customEndDate) {
      start = customStartDate
      end = customEndDate
      days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
    } else {
      days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90
      start = subDays(now, days)
      end = now
    }

    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
      previousStartDate: format(subDays(start, days), 'yyyy-MM-dd'),
      dateRangeDays: days
    }
  }, [dateRange, customStartDate, customEndDate])

  // Fetch insights data from Supabase
  const { data: insights, isLoading, refetch } = useQuery({
    queryKey: ['gmb-insights', startDate, endDate, selectedLocationFilter],
    queryFn: async () => {
      let query = supabase
        .from('gmb_insights')
        .select('*')
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true })

      if (selectedLocationFilter !== 'all') {
        query = query.eq('gmb_location_id', selectedLocationFilter)
      }

      const { data, error } = await query

      if (error) throw error
      return data as InsightRow[]
    }
  })

  // Fetch previous period for comparison
  const { data: previousInsights } = useQuery({
    queryKey: ['gmb-insights-previous', previousStartDate, startDate, selectedLocationFilter],
    queryFn: async () => {
      let query = supabase
        .from('gmb_insights')
        .select('*')
        .gte('date', previousStartDate)
        .lt('date', startDate)

      if (selectedLocationFilter !== 'all') {
        query = query.eq('gmb_location_id', selectedLocationFilter)
      }

      const { data, error } = await query

      if (error) throw error
      return data as InsightRow[]
    }
  })

  // Fetch location data including search keywords
  const { data: gmbLocations } = useQuery({
    queryKey: ['gmb-locations-for-insights'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gmb_locations')
        .select('gmb_api_location_id, city, location_name, franchise_region, search_keywords, keywords_updated_at')

      if (error) throw error
      return data
    }
  })

  // Fetch performance goals
  const { data: goals } = useQuery({
    queryKey: ['gmb-performance-goals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gmb_performance_goals')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (error) {
        // Table might not exist yet - return empty array
        console.warn('Goals table not available:', error.message)
        return []
      }
      return data as PerformanceGoal[]
    }
  })

  // Create goal mutation
  const createGoalMutation = useMutation({
    mutationFn: async (goal: Omit<PerformanceGoal, 'id' | 'created_at' | 'is_active'>) => {
      const { data, error } = await supabase
        .from('gmb_performance_goals')
        .insert({
          ...goal,
          is_active: true
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmb-performance-goals'] })
      toast.success('Goal Created', 'Your performance goal has been set')
      setIsGoalModalOpen(false)
      resetGoalForm()
    },
    onError: (error) => {
      toast.error('Failed to Create Goal', error instanceof Error ? error.message : 'Unknown error')
    }
  })

  // Update goal mutation
  const updateGoalMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PerformanceGoal> & { id: string }) => {
      const { data, error } = await supabase
        .from('gmb_performance_goals')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmb-performance-goals'] })
      toast.success('Goal Updated', 'Your performance goal has been updated')
      setIsGoalModalOpen(false)
      setEditingGoal(null)
      resetGoalForm()
    },
    onError: (error) => {
      toast.error('Failed to Update Goal', error instanceof Error ? error.message : 'Unknown error')
    }
  })

  // Delete goal mutation
  const deleteGoalMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('gmb_performance_goals')
        .update({ is_active: false })
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmb-performance-goals'] })
      toast.success('Goal Removed', 'Your performance goal has been removed')
    },
    onError: (error) => {
      toast.error('Failed to Remove Goal', error instanceof Error ? error.message : 'Unknown error')
    }
  })

  // Reset goal form
  const resetGoalForm = () => {
    setGoalForm({
      metric_type: 'views_search',
      target_value: '',
      period_type: 'monthly',
      gmb_location_id: 'all',
      notes: ''
    })
  }

  // Handle goal form submission
  const handleGoalSubmit = () => {
    const targetValue = parseInt(goalForm.target_value)
    if (isNaN(targetValue) || targetValue <= 0) {
      toast.error('Invalid Target', 'Please enter a positive number for the target value')
      return
    }

    const goalData = {
      metric_type: goalForm.metric_type,
      target_value: targetValue,
      period_type: goalForm.period_type,
      gmb_location_id: goalForm.gmb_location_id === 'all' ? null : goalForm.gmb_location_id,
      notes: goalForm.notes || null
    }

    if (editingGoal) {
      updateGoalMutation.mutate({ id: editingGoal.id, ...goalData })
    } else {
      createGoalMutation.mutate(goalData)
    }
  }

  // Open edit modal for existing goal
  const handleEditGoal = (goal: PerformanceGoal) => {
    setEditingGoal(goal)
    setGoalForm({
      metric_type: goal.metric_type,
      target_value: goal.target_value.toString(),
      period_type: goal.period_type,
      gmb_location_id: goal.gmb_location_id || 'all',
      notes: goal.notes || ''
    })
    setIsGoalModalOpen(true)
  }

  // Create location name map
  const locationNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    gmbLocations?.forEach(loc => {
      if (loc.gmb_api_location_id) {
        map[loc.gmb_api_location_id] = loc.city || loc.franchise_region || loc.location_name || 'Unknown'
      }
    })
    return map
  }, [gmbLocations])

  // Calculate goal progress based on current metrics
  const getGoalProgress = useCallback((goal: PerformanceGoal) => {
    // Filter insights by goal's location if specified
    const relevantInsights = insights?.filter(row =>
      !goal.gmb_location_id || row.gmb_location_id === goal.gmb_location_id
    ) || []

    // Calculate current value based on metric type
    let currentValue = 0
    switch (goal.metric_type) {
      case 'views_search':
        currentValue = relevantInsights.reduce((sum, row) => sum + (row.views_search || 0), 0)
        break
      case 'views_maps':
        currentValue = relevantInsights.reduce((sum, row) => sum + (row.views_maps || 0), 0)
        break
      case 'actions_website':
        currentValue = relevantInsights.reduce((sum, row) => sum + (row.actions_website || 0), 0)
        break
      case 'actions_phone':
        currentValue = relevantInsights.reduce((sum, row) => sum + (row.actions_phone || 0), 0)
        break
      case 'actions_directions':
        currentValue = relevantInsights.reduce((sum, row) => sum + (row.actions_directions || 0), 0)
        break
      case 'total_views':
        currentValue = relevantInsights.reduce((sum, row) =>
          sum + (row.views_search || 0) + (row.views_maps || 0), 0)
        break
      case 'total_actions':
        currentValue = relevantInsights.reduce((sum, row) =>
          sum + (row.actions_website || 0) + (row.actions_phone || 0) + (row.actions_directions || 0), 0)
        break
    }

    const progressPercent = Math.min(100, Math.round((currentValue / goal.target_value) * 100))
    return { currentValue, progressPercent }
  }, [insights])

  // Aggregate search keywords across all locations
  const aggregatedKeywords = useMemo(() => {
    const keywordMap: Record<string, number> = {}

    gmbLocations?.forEach(loc => {
      if (loc.search_keywords && Array.isArray(loc.search_keywords)) {
        (loc.search_keywords as SearchKeyword[]).forEach(kw => {
          if (kw.keyword) {
            keywordMap[kw.keyword] = (keywordMap[kw.keyword] || 0) + (kw.impressions || 0)
          }
        })
      }
    })

    return Object.entries(keywordMap)
      .map(([keyword, impressions]) => ({ keyword, impressions }))
      .sort((a, b) => b.impressions - a.impressions)
      .slice(0, 15) // Top 15 keywords
  }, [gmbLocations])

  // Fetch data coverage stats (all-time data range)
  const { data: coverageStats } = useQuery({
    queryKey: ['gmb-insights-coverage'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gmb_insights')
        .select('date')
        .order('date', { ascending: true })

      if (error) throw error
      if (!data || data.length === 0) return null

      const dates = data.map(d => new Date(d.date))
      const earliest = dates[0]
      const latest = dates[dates.length - 1]
      const totalDays = data.length
      const dayRange = Math.ceil((latest.getTime() - earliest.getTime()) / (1000 * 60 * 60 * 24)) + 1
      const uniqueLocations = new Set(data.map((d: any) => d.gmb_location_id)).size

      return {
        earliestDate: earliest,
        latestDate: latest,
        totalDataPoints: totalDays,
        expectedDays: dayRange * (gmbLocations?.length || 6),
        coveragePercent: Math.round((totalDays / (dayRange * (gmbLocations?.length || 6))) * 100),
        dayRange
      }
    }
  })

  // Calculate aggregated metrics
  const metrics: AggregatedMetrics = useMemo(() => {
    const current = insights || []
    const previous = previousInsights || []

    return {
      totalViewsSearch: current.reduce((sum, row) => sum + (row.views_search || 0), 0),
      totalViewsMaps: current.reduce((sum, row) => sum + (row.views_maps || 0), 0),
      totalActionsWebsite: current.reduce((sum, row) => sum + (row.actions_website || 0), 0),
      totalActionsPhone: current.reduce((sum, row) => sum + (row.actions_phone || 0), 0),
      totalActionsDirections: current.reduce((sum, row) => sum + (row.actions_directions || 0), 0),
      totalPhotoViews: current.reduce((sum, row) => sum + (row.photo_views || 0), 0),
      previousViewsSearch: previous.reduce((sum, row) => sum + (row.views_search || 0), 0),
      previousViewsMaps: previous.reduce((sum, row) => sum + (row.views_maps || 0), 0),
      previousActionsWebsite: previous.reduce((sum, row) => sum + (row.actions_website || 0), 0),
      previousActionsPhone: previous.reduce((sum, row) => sum + (row.actions_phone || 0), 0),
      previousActionsDirections: previous.reduce((sum, row) => sum + (row.actions_directions || 0), 0),
    }
  }, [insights, previousInsights])

  // Calculate percentage change
  const calcChange = (current: number, previous: number): number => {
    if (previous === 0) return current > 0 ? 100 : 0
    return Math.round(((current - previous) / previous) * 100)
  }

  // Group data by date for chart
  const chartData = useMemo(() => {
    if (!insights) return []

    const byDate: Record<string, {
      date: string
      displayDate: string
      views: number
      actions: number
    }> = {}

    insights.forEach(row => {
      if (!byDate[row.date]) {
        byDate[row.date] = {
          date: row.date,
          displayDate: format(new Date(row.date), 'MMM d'),
          views: 0,
          actions: 0
        }
      }
      byDate[row.date].views += (row.views_search || 0) + (row.views_maps || 0)
      byDate[row.date].actions += (row.actions_website || 0) + (row.actions_phone || 0) + (row.actions_directions || 0)
    })

    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date))
  }, [insights])

  // Group data by location
  const locationData = useMemo(() => {
    if (!insights) return []

    const byLocation: Record<string, {
      locationId: string
      views: number
      actions: number
    }> = {}

    insights.forEach(row => {
      if (!byLocation[row.gmb_location_id]) {
        byLocation[row.gmb_location_id] = { locationId: row.gmb_location_id, views: 0, actions: 0 }
      }
      byLocation[row.gmb_location_id].views += (row.views_search || 0) + (row.views_maps || 0)
      byLocation[row.gmb_location_id].actions += (row.actions_website || 0) + (row.actions_phone || 0) + (row.actions_directions || 0)
    })

    return Object.values(byLocation)
      .sort((a, b) => b.views - a.views)
  }, [insights])

  // Handle sync with toast feedback
  const handleSync = async (days?: number) => {
    const daysToSync = days || syncDays
    setIsSyncing(true)
    try {
      toast.info('Syncing...', `Fetching ${daysToSync} days of insights for ${gmbLocations?.length || 0} locations`)
      await syncInsights(undefined, daysToSync)
      await refetch()
      toast.success('Sync Complete', `Synced ${daysToSync} days of insights for ${gmbLocations?.length || 0} locations`)
    } catch (error) {
      console.error('Error syncing insights:', error)
      toast.error('Sync Failed', error instanceof Error ? error.message : 'Failed to sync insights')
    } finally {
      setIsSyncing(false)
    }
  }

  // Handle location-specific sync with toast feedback
  const handleLocationSync = async (locationId: string, locationName: string, days: number = 180) => {
    setSyncingLocationId(locationId)
    try {
      toast.info('Syncing Location...', `Fetching ${days} days of insights for ${locationName}`)
      await syncInsights([locationId], days)
      await refetch()
      toast.success('Location Sync Complete', `Synced ${days} days of insights for ${locationName}`)
    } catch (error) {
      console.error('Error syncing location insights:', error)
      toast.error('Location Sync Failed', error instanceof Error ? error.message : `Failed to sync ${locationName}`)
    } finally {
      setSyncingLocationId(null)
    }
  }

  // Per-location coverage stats
  const { data: locationCoverageStats } = useQuery({
    queryKey: ['gmb-insights-location-coverage'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gmb_insights')
        .select('gmb_location_id, date')
        .order('date', { ascending: true })

      if (error) throw error
      if (!data || data.length === 0) return {}

      // Group by location and calculate coverage
      const byLocation: Record<string, { earliest: Date; latest: Date; count: number }> = {}

      data.forEach(row => {
        const locId = row.gmb_location_id
        const date = new Date(row.date)

        if (!byLocation[locId]) {
          byLocation[locId] = { earliest: date, latest: date, count: 1 }
        } else {
          byLocation[locId].count++
          if (date < byLocation[locId].earliest) byLocation[locId].earliest = date
          if (date > byLocation[locId].latest) byLocation[locId].latest = date
        }
      })

      return byLocation
    },
    enabled: !!gmbLocations && gmbLocations.length > 0
  })

  // CSV Export function
  const handleExportCSV = useCallback(() => {
    if (!insights || insights.length === 0) {
      toast.warning('No Data', 'No insights data available to export')
      return
    }

    const headers = [
      'Date',
      'Location',
      'Search Views',
      'Maps Views',
      'Website Clicks',
      'Phone Calls',
      'Direction Requests',
      'Total Views',
      'Total Actions'
    ]

    const rows = insights.map(row => [
      row.date,
      locationNameMap[row.gmb_location_id] || 'Unknown',
      row.views_search || 0,
      row.views_maps || 0,
      row.actions_website || 0,
      row.actions_phone || 0,
      row.actions_directions || 0,
      (row.views_search || 0) + (row.views_maps || 0),
      (row.actions_website || 0) + (row.actions_phone || 0) + (row.actions_directions || 0)
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `gmb-insights-${startDate}-to-${endDate}.csv`
    link.click()
    URL.revokeObjectURL(link.href)

    toast.success('Export Complete', `Downloaded ${insights.length} records`)
  }, [insights, locationNameMap, startDate, endDate, toast])

  // Date validation for custom range (max 18 months)
  const maxDate = new Date()
  const minDate = subMonths(maxDate, 18)

  // Metric card component
  const MetricCard = ({
    title,
    value,
    change,
    icon: Icon,
    description
  }: {
    title: string
    value: number
    change: number
    icon: React.ElementType
    description: string
  }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value.toLocaleString()}</div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {change >= 0 ? (
            <ArrowUpRight className="h-3 w-3 text-green-500" />
          ) : (
            <ArrowDownRight className="h-3 w-3 text-red-500" />
          )}
          <span className={change >= 0 ? 'text-green-500' : 'text-red-500'}>
            {change >= 0 ? '+' : ''}{change}%
          </span>
          <span>vs previous {dateRangeDays} days</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </CardContent>
    </Card>
  )

  // Custom tooltip for Recharts
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border rounded-lg shadow-lg p-3">
          <p className="font-medium text-sm mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{entry.name}:</span>
              <span className="font-medium">{entry.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )
    }
    return null
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Performance Insights</h2>
          <p className="text-sm text-muted-foreground">
            Track views and customer actions across your locations
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Date Range Selector */}
          <Select value={dateRange} onValueChange={(v) => setDateRange(v as DateRange)}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>

          {/* Custom Date Range Pickers */}
          {dateRange === 'custom' && (
            <>
              <DatePicker
                date={customStartDate}
                onDateChange={setCustomStartDate}
                placeholder="Start date"
                disabled={(date) => isAfter(date, maxDate) || isBefore(date, minDate)}
                className="w-[140px]"
              />
              <span className="text-muted-foreground">to</span>
              <DatePicker
                date={customEndDate}
                onDateChange={setCustomEndDate}
                placeholder="End date"
                disabled={(date) => isAfter(date, maxDate) || isBefore(date, minDate) || (customStartDate ? isBefore(date, customStartDate) : false)}
                className="w-[140px]"
              />
            </>
          )}

          {/* Location Filter */}
          <Select value={selectedLocationFilter} onValueChange={setSelectedLocationFilter}>
            <SelectTrigger className="w-[180px]">
              <MapPin className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {gmbLocations?.map(loc => (
                <SelectItem key={loc.gmb_api_location_id} value={loc.gmb_api_location_id || ''}>
                  {loc.city || loc.franchise_region || 'Unknown'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Export Button */}
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>

          {/* Sync Controls */}
          <div className="flex items-center gap-1">
            <Select value={syncDays.toString()} onValueChange={(v) => setSyncDays(parseInt(v))}>
              <SelectTrigger className="w-[100px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">6 months</SelectItem>
                <SelectItem value="365">1 year</SelectItem>
                <SelectItem value="540">18 months</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => handleSync()} disabled={isSyncing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync'}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Metrics Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Search Views"
            value={metrics.totalViewsSearch}
            change={calcChange(metrics.totalViewsSearch, metrics.previousViewsSearch)}
            icon={Eye}
            description="Impressions from Google Search"
          />
          <MetricCard
            title="Maps Views"
            value={metrics.totalViewsMaps}
            change={calcChange(metrics.totalViewsMaps, metrics.previousViewsMaps)}
            icon={MapPin}
            description="Impressions from Google Maps"
          />
          <MetricCard
            title="Website Clicks"
            value={metrics.totalActionsWebsite}
            change={calcChange(metrics.totalActionsWebsite, metrics.previousActionsWebsite)}
            icon={Globe}
            description="Clicks to your website"
          />
          <MetricCard
            title="Phone Calls"
            value={metrics.totalActionsPhone}
            change={calcChange(metrics.totalActionsPhone, metrics.previousActionsPhone)}
            icon={Phone}
            description="Calls from your listing"
          />
        </div>
      )}

      {/* Secondary Metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard
          title="Direction Requests"
          value={metrics.totalActionsDirections}
          change={calcChange(metrics.totalActionsDirections, metrics.previousActionsDirections)}
          icon={MapPin}
          description="Navigation requests"
        />
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Views</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(metrics.totalViewsSearch + metrics.totalViewsMaps).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Search + Maps combined
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Actions</CardTitle>
            <MousePointer className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(metrics.totalActionsWebsite + metrics.totalActionsPhone + metrics.totalActionsDirections).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Website + Phone + Directions
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Performance Trend Chart with Recharts */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Trend</CardTitle>
          <CardDescription>
            Daily views and actions over the selected period
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorActions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="displayDate"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="views"
                  name="Views"
                  stroke="hsl(var(--primary))"
                  fillOpacity={1}
                  fill="url(#colorViews)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="actions"
                  name="Actions"
                  stroke="hsl(142, 76%, 36%)"
                  fillOpacity={1}
                  fill="url(#colorActions)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No insights data available for this period</p>
              <p className="text-sm mt-1">Run a sync to fetch the latest metrics</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search Keywords Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Top Search Keywords
              </CardTitle>
              <CardDescription>
                Search terms customers use to find your business on Google
              </CardDescription>
            </div>
            {gmbLocations?.[0]?.keywords_updated_at && (
              <span className="text-xs text-muted-foreground">
                Updated {format(new Date(gmbLocations[0].keywords_updated_at), 'MMM d, yyyy')}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {aggregatedKeywords.length > 0 ? (
            <div className="space-y-3">
              {aggregatedKeywords.map((kw, index) => {
                const maxImpressions = aggregatedKeywords[0]?.impressions || 1
                const widthPercent = (kw.impressions / maxImpressions) * 100

                return (
                  <div key={kw.keyword} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground w-6">{index + 1}.</span>
                        <span className="font-medium">"{kw.keyword}"</span>
                      </div>
                      <span className="text-muted-foreground">
                        {kw.impressions.toLocaleString()} impressions
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden ml-8">
                      <div
                        className="h-full bg-primary/60 transition-all duration-300"
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No search keywords available</p>
              <p className="text-sm mt-1">Run a sync to fetch keyword data</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Location Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Performance by Location</CardTitle>
          <CardDescription>
            Compare metrics across your business locations. Locations with less than 90 days of data can be synced individually.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {locationData.length > 0 ? (
            <div className="space-y-4">
              {locationData.map(loc => {
                const maxViews = Math.max(...locationData.map(l => l.views))
                const viewsPercent = maxViews > 0 ? (loc.views / maxViews) * 100 : 0
                const locationName = locationNameMap[loc.locationId] || 'Unknown Location'
                const coverage = locationCoverageStats?.[loc.locationId]
                const daysCovered = coverage?.count || 0
                const needsSync = daysCovered < 90
                const isThisLocationSyncing = syncingLocationId === loc.locationId

                return (
                  <div key={loc.locationId} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{locationName}</span>
                        {needsSync && (
                          <Badge variant="outline" className="text-amber-600 border-amber-500 text-xs">
                            {daysCovered} days
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex gap-4 text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {loc.views.toLocaleString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <MousePointer className="h-3 w-3" />
                            {loc.actions.toLocaleString()}
                          </span>
                        </div>
                        {needsSync && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            disabled={isThisLocationSyncing || isSyncing}
                            onClick={() => handleLocationSync(loc.locationId, locationName, 180)}
                          >
                            {isThisLocationSyncing ? (
                              <RefreshCw className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Sync 6mo
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all duration-300"
                        style={{ width: `${viewsPercent}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No location data available</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Coverage Card */}
      <Card className={coverageStats && coverageStats.coveragePercent < 50 ? 'border-amber-500' : ''}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="h-4 w-4" />
            Data Coverage
            {coverageStats && coverageStats.coveragePercent < 50 && (
              <Badge variant="outline" className="ml-2 text-amber-600 border-amber-500">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Incomplete
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {coverageStats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Earliest Data</p>
                <p className="text-sm font-medium">{format(coverageStats.earliestDate, 'MMM d, yyyy')}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Latest Data</p>
                <p className="text-sm font-medium">{format(coverageStats.latestDate, 'MMM d, yyyy')}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Data Points</p>
                <p className="text-sm font-medium">{coverageStats.totalDataPoints.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Coverage</p>
                <p className={`text-sm font-medium ${coverageStats.coveragePercent < 50 ? 'text-amber-600' : coverageStats.coveragePercent < 80 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {coverageStats.coveragePercent}%
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              <span>No insights data. Run a sync to fetch data from Google.</span>
            </div>
          )}
          {coverageStats && coverageStats.coveragePercent < 80 && (
            <p className="text-xs text-muted-foreground mt-3">
              💡 Select "6 months" or "18 months" in the sync dropdown and click Sync to backfill historical data.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Performance Goals Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Performance Goals
              </CardTitle>
              <CardDescription>
                Set KPI targets and track progress toward your goals
              </CardDescription>
            </div>
            <Dialog open={isGoalModalOpen} onOpenChange={(open) => {
              setIsGoalModalOpen(open)
              if (!open) {
                setEditingGoal(null)
                resetGoalForm()
              }
            }}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Goal
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{editingGoal ? 'Edit Goal' : 'Create Performance Goal'}</DialogTitle>
                  <DialogDescription>
                    Set a target for a specific metric to track your progress
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  {/* Metric Type */}
                  <div className="grid gap-2">
                    <Label htmlFor="metric_type">Metric</Label>
                    <Select
                      value={goalForm.metric_type}
                      onValueChange={(v) => setGoalForm(prev => ({ ...prev, metric_type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {METRIC_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <opt.icon className="h-4 w-4" />
                              {opt.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Target Value */}
                  <div className="grid gap-2">
                    <Label htmlFor="target_value">Target Value</Label>
                    <Input
                      id="target_value"
                      type="number"
                      min="1"
                      placeholder="e.g., 1000"
                      value={goalForm.target_value}
                      onChange={(e) => setGoalForm(prev => ({ ...prev, target_value: e.target.value }))}
                    />
                  </div>

                  {/* Period Type */}
                  <div className="grid gap-2">
                    <Label htmlFor="period_type">Period</Label>
                    <Select
                      value={goalForm.period_type}
                      onValueChange={(v: 'weekly' | 'monthly' | 'quarterly') =>
                        setGoalForm(prev => ({ ...prev, period_type: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Location */}
                  <div className="grid gap-2">
                    <Label htmlFor="location">Location</Label>
                    <Select
                      value={goalForm.gmb_location_id}
                      onValueChange={(v) => setGoalForm(prev => ({ ...prev, gmb_location_id: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All Locations" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Locations (Company-wide)</SelectItem>
                        {gmbLocations?.map(loc => (
                          <SelectItem key={loc.gmb_api_location_id} value={loc.gmb_api_location_id || ''}>
                            {loc.city || loc.franchise_region || 'Unknown'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Notes */}
                  <div className="grid gap-2">
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Input
                      id="notes"
                      placeholder="e.g., Q1 growth target"
                      value={goalForm.notes}
                      onChange={(e) => setGoalForm(prev => ({ ...prev, notes: e.target.value }))}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsGoalModalOpen(false)
                      setEditingGoal(null)
                      resetGoalForm()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleGoalSubmit}
                    disabled={createGoalMutation.isPending || updateGoalMutation.isPending}
                  >
                    {(createGoalMutation.isPending || updateGoalMutation.isPending) && (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    {editingGoal ? 'Update Goal' : 'Create Goal'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {goals && goals.length > 0 ? (
            <div className="space-y-4">
              {goals.map(goal => {
                const { currentValue, progressPercent } = getGoalProgress(goal)
                const metricOption = METRIC_OPTIONS.find(m => m.value === goal.metric_type)
                const MetricIcon = metricOption?.icon || Target

                // Determine progress color
                let progressColor = 'bg-red-500'
                if (progressPercent >= 80) progressColor = 'bg-green-500'
                else if (progressPercent >= 50) progressColor = 'bg-yellow-500'

                return (
                  <div key={goal.id} className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <MetricIcon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {metricOption?.label || goal.metric_type}
                            <Badge variant="outline" className="text-xs">
                              {goal.period_type}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {goal.gmb_location_id
                              ? locationNameMap[goal.gmb_location_id] || 'Unknown Location'
                              : 'All Locations'}
                            {goal.notes && ` • ${goal.notes}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEditGoal(goal)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => deleteGoalMutation.mutate(goal.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {currentValue.toLocaleString()} / {goal.target_value.toLocaleString()}
                        </span>
                        <span className={`font-medium ${
                          progressPercent >= 80 ? 'text-green-600' :
                          progressPercent >= 50 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {progressPercent}%
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full ${progressColor} transition-all duration-500`}
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No performance goals set</p>
              <p className="text-sm mt-1">Click "Add Goal" to set your first KPI target</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
